import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/apiService';
import { APP_NAME } from '../config/frontend';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default fallback values
  const defaultSettings = {
    company_name: APP_NAME,
    currency_symbol: 'Ghs',
    currency: 'GHS',
    company_email: 'info@nexapos.com',
    company_phone: '+233 548 104 944',
    company_address: '123 Business Street, Accra, Ghana',
    tax_rate: 15.0,
    tax_enabled: true,
    low_stock_alert: 10,
    receipt_footer: 'Thank you for your business!'
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.get('/settings');
      
      if (response.success) {
        setSettings({ ...defaultSettings, ...response.data });
      } else {
        console.warn('Failed to fetch settings, using defaults');
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError(error.message);
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      setError(null);
      
      const response = await apiService.post('/settings', newSettings);
      
      if (response.success) {
        setSettings({ ...defaultSettings, ...response.data });
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  const getSetting = (key, fallback = null) => {
    return settings[key] !== undefined ? settings[key] : fallback;
  };

  const formatCurrency = (amount, showSymbol = true, colorClass = '') => {
    const symbol = getSetting('currency_symbol', 'Ghs');
    const numAmount = parseFloat(amount || 0);
    
    // Format with thousands separators
    const formattedAmount = numAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    const formatted = showSymbol ? `${symbol} ${formattedAmount}` : formattedAmount;
    
    // Return with color class if provided
    return colorClass ? (
      <span className={colorClass}>{formatted}</span>
    ) : formatted;
  };

  const getCompanyName = () => {
    return getSetting('company_name', APP_NAME);
  };

  const getCurrencySymbol = () => {
    return getSetting('currency_symbol', 'Ghs');
  };

  const getTaxRate = () => {
    return getSetting('tax_rate', 15.0);
  };

  const isTaxEnabled = () => {
    return getSetting('tax_enabled', true);
  };

  const getLowStockAlert = () => {
    return getSetting('low_stock_alert', 10);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const value = {
    settings,
    loading,
    error,
    fetchSettings,
    updateSettings,
    getSetting,
    formatCurrency,
    getCompanyName,
    getCurrencySymbol,
    getTaxRate,
    isTaxEnabled,
    getLowStockAlert,
    // Company info
    companyName: getCompanyName(),
    companyEmail: getSetting('company_email'),
    companyPhone: getSetting('company_phone'),
    companyAddress: getSetting('company_address'),
    companyWebsite: getSetting('company_website'),
    taxNumber: getSetting('tax_number'),
    // Currency info
    currency: getSetting('currency', 'GHS'),
    currencySymbol: getCurrencySymbol(),
    // Tax info
    taxRate: getTaxRate(),
    taxEnabled: isTaxEnabled(),
    // Other settings
    lowStockAlert: getLowStockAlert(),
    receiptFooter: getSetting('receipt_footer'),
    autoBackup: getSetting('auto_backup', true),
    emailNotifications: getSetting('email_notifications', true),
    smsNotifications: getSetting('sms_notifications', false),
    lowStockNotifications: getSetting('low_stock_notifications', true)
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;
